import posthog from "posthog-js";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import Loader from "@/app/components/Loader";
import { useAuth } from "@/app/context/Auth/AuthContext";

import FitCheckLoading from "./components/FitCheckLoading";
import FitCheckReport from "./components/FitCheckReport";
import SelectDSP from "./components/SelectDSP";
import { FitCheckAssessment, useCreateFitCheckMutation, useFitCheck } from "./config/api";

/**
 * Calculate match level from FitCheck assessment
 */
function getMatchLevel(assessment: FitCheckAssessment): "high" | "partial" | "low" {
	const { baselineCheck } = assessment;
	const statuses = [
		baselineCheck.education.status,
		baselineCheck.experience.status,
		baselineCheck.skills.status,
		baselineCheck.certification.status,
	];

	const meetCount = statuses.filter((s) => s === "MEET").length;
	const missingCount = statuses.filter((s) => s === "MISSING").length;

	if (meetCount >= 3) return "high";
	if (missingCount >= 3) return "low";
	return "partial";
}

/**
 * Track fitcheck_completed analytics event
 */
function trackFitCheckCompleted(
	assessment: FitCheckAssessment,
	jobDetails: { _id: string; jobTitle: string; company?: { company_name?: string; _id?: string } },
) {
	const matchLevel = getMatchLevel(assessment);
	posthog.capture("fitcheck_completed", {
		jobId: jobDetails._id,
		jobTitle: jobDetails.jobTitle,
		companyName: jobDetails.company?.company_name,
		companyId: jobDetails.company?._id,
		matchLevel,
	});
}

export type STEPS = "SELECT_DSP" | "LOADING" | "RESULTS";

interface Company {
	_id: string;
	company_name: string;
	linkedin_url: string;
	logo_url: string;
	company_website: string;
	company_industry: string;
	company_size_approx: string;
	headquarters: string;
	type: string;
	locations: string[];
	about_us_summary: string;
}

interface DateInfo {
	day: number;
	month: number;
	year: number;
}

interface JobLocation {
	city: string;
	region: string;
	country: string;
}

interface QualificationItem {
	bp: boolean;
	value: string;
	_id?: string;
}

interface JobDescription {
	lookingToHire: string;
	responsibilities: QualificationItem[];
	qualifications: QualificationItem[];
}

interface ExperienceQualification {
	minimumLevelOfExperience: string;
	preferredYearsOfExperience: number;
}

interface EducationQualification {
	minEducationLevel: string;
	preferredEducationLevel: string;
	relevantFieldOfStudies: string[];
	relevantMajors: string[];
}

interface Salary {
	payOption: string;
	minPay: number;
	maxPay: number;
	payCurrency: string;
	payFrequency: string;
}

export interface JobDetails {
	_id: string;
	jobProcessingType: string;
	companyName: string;
	company: Company;
	jobTitle: string;
	startingDate: DateInfo;
	deadlineDate: DateInfo;
	numberOfOpening: number;
	jobStreetAddress: string;
	jobLocation: JobLocation;
	employmentType: string;
	jobExpertise: string;
	jobDescription: JobDescription;
	experienceQualification: ExperienceQualification;
	skillsQualifications: QualificationItem[];
	educationQualification: EducationQualification;
	salary: Salary;
	jobStatus: string;
	additionalInformation: string;
	applicationProcess: string;
	lastUpdatedAt: string;
	closeAt: string;
}

interface FitCheckProps {
	jobDetails: JobDetails;
}

function FitCheck({ jobDetails }: FitCheckProps) {
	const [currentStep, setCurrentStep] = useState<STEPS>("SELECT_DSP");
	const [fitCheckData, setFitCheckData] = useState<FitCheckAssessment | null>(null);
	const [isCheckingExisting, setIsCheckingExisting] = useState(true);

	const { userData } = useAuth();

	// Check for existing FitCheck on mount
	const { data: existingFitCheck, isLoading: isLoadingExisting } = useFitCheck(userData?.userID || "", jobDetails._id);

	// Mutation to create new FitCheck
	const { mutate: createFitCheck } = useCreateFitCheckMutation();

	// Check for existing FitCheck on component mount
	useEffect(() => {
		if (!isLoadingExisting) {
			setIsCheckingExisting(false);

			if (existingFitCheck?.fitCheck) {
				// Existing FitCheck found, show results
				setFitCheckData(existingFitCheck.fitCheck);
				setCurrentStep("RESULTS");
				// Track analytics for returning user viewing existing results
				trackFitCheckCompleted(existingFitCheck.fitCheck, jobDetails);
			} else {
				// No existing FitCheck, show DSP selection
				setCurrentStep("SELECT_DSP");
			}
		}
	}, [existingFitCheck, isLoadingExisting, jobDetails]);

	function runFitCheck(resumeURL: string) {
		if (!userData) {
			toast.error("User data not available");
			return;
		}

		setCurrentStep("LOADING");

		const user = {
			firstName: userData.firstName,
			lastName: userData.lastName,
			email: userData.email,
			userId: userData.userID,
		};

		createFitCheck(
			{
				userId: userData.userID,
				jobId: jobDetails._id,
				resumeURL,
				user,
				jobDetails: jobDetails as unknown as Record<string, unknown>,
			},
			{
				onSuccess: (response) => {
					setFitCheckData(response.fitCheck);
					setCurrentStep("RESULTS");
					// Track analytics for newly completed FitCheck
					trackFitCheckCompleted(response.fitCheck, jobDetails);
					toast.success("FitCheck completed successfully!");
				},
				onError: (error: Error) => {
					setCurrentStep("SELECT_DSP");
					const errorMessage =
						(error as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message ||
						"Failed to run FitCheck. Please try again.";
					toast.error(errorMessage);
				},
			},
		);
	}

	// Show initial loading state while checking for existing FitCheck
	if (isCheckingExisting) {
		return (
			<div className="flex flex-col items-center justify-center min-h-[200px] py-8 -gap-4">
				<Loader />
				<div className="text-center">
					<p className="text-sm text-gray-600">Checking for existing assessments...</p>
				</div>
			</div>
		);
	}

	return (
		<div>
			{currentStep === "SELECT_DSP" && <SelectDSP runFitCheck={runFitCheck} />}
			{currentStep === "LOADING" && <FitCheckLoading />}
			{currentStep === "RESULTS" && fitCheckData && <FitCheckReport fitCheck={fitCheckData} />}
		</div>
	);
}

export default FitCheck;
